import { useAxios } from '@healthblocks-io/core'
import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import DOMPurify from 'dompurify'
import { useNavigate, useOutletContext } from 'react-router-dom'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Markdown } from '@/@bothrs-ui/web/components/markdown/Markdown'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { H4 } from '@/@bothrs-ui/web/components/typography/Heading'
import { globalDefinitions } from '@/defined-translations'
import { mixpanel } from '@/mixpanel'
import { formatLocale } from '@/modules/shared/helpers/locale'
import { BadgeService } from '@/modules/shared/services/badgeService'
import { RelatedTriggers } from '@/modules/timeline/components/related-triggers/RelatedTriggers'
import { useLoggedInForMandatee } from '@/modules/trust/hooks/useLoggedInForMandatee'
import { isIntramuros } from '@/utils/intramuros'
import { useTaskStatusMetaData } from '../../hooks/useTaskStatusMetaData'
import { TaskDetailLayout } from '../detail-layout/TaskDetailLayout'
import { StAwellHtml } from './MessageDetail.styled'

import type { TaskOutletContext } from '../../pages/TaskRoot.page'
import type { MessageTask } from '../../types/task.types'

type Props = {
  task: MessageTask
  refetch: () => void
}

export const MessageDetail = ({ task, refetch }: Props) => {
  const navigate = useNavigate()

  const axios = useAxios()
  const { daysTillExpiry } = useTaskStatusMetaData(task)
  const { reloadAndSelectTask } = useOutletContext<TaskOutletContext>()

  const isLoggedInForMandatee = useLoggedInForMandatee()

  const isCompleted = task.status === 'completed'
  const isExpired = task.status === 'expired'

  const handleCtaPress = async () => {
    mixpanel.track('message_confirmed', {
      is_intramuros: isIntramuros,
      is_mandate: isLoggedInForMandatee,
    })

    if (isCompleted || isExpired) {
      if (isIntramuros) {
        navigate('../..', { relative: 'path', state: { taskId: task.id } })
      } else {
        navigate('..', { relative: 'path', state: { taskId: task.id } })
      }
      return
    }

    try {
      await axios.patch(`/careplan-activity/${task.careplanActivityId}`, {
        status: 'completed',
        meta: {
          readAt: new Date().toISOString(),
        },
      })
      // Update badge count since activity was completed
      BadgeService.decrementBadgeCount()

      // For Action messages, navigate to resolved tab with this task selected
      if (task.messageType === 'Action') {
        refetch()
        reloadAndSelectTask(task.id)
      } else {
        refetch()
      }
    } catch (error) {
      console.error(error)
    }
  }

  const getStatusDisplay = (): string => {
    if (isCompleted) {
      return i18n._(globalDefinitions.task.Message.completed, {
        date: formatLocale(task.updatedAt, 'dd/MM/yyyy'),
      })
    }

    if (isExpired) {
      return i18n._(globalDefinitions.task.Message.expired)
    }

    if (daysTillExpiry === 0) {
      return i18n._(globalDefinitions.task.Message.expires_today)
    }

    if (daysTillExpiry === 1) {
      return i18n._(globalDefinitions.task.Message.expires_in_one_day)
    }

    if (daysTillExpiry && daysTillExpiry > 1) {
      return i18n._(globalDefinitions.task.Message.expires_in_x_days, {
        count: daysTillExpiry,
      })
    }

    return i18n._(globalDefinitions.task.no_expiry, {
      date: formatLocale(task.createdAt, 'dd/MM/yyyy'),
    })
  }

  return (
    <TaskDetailLayout>
      <TaskDetailLayout.Header task={task} statusLabel={getStatusDisplay()} />
      <TaskDetailLayout.Content>
        <FlexColumn style={{ flex: 1 }}>
          {task.title ? <H4>{task.title}</H4> : null}
          {task.message ? (
            <Markdown content={task.message} />
          ) : task.body ? (
            <StAwellHtml
              // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized with DOMPurify
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(task.body),
              }}
            />
          ) : null}

          {/* Related activities */}
          {task?.trigger && <RelatedTriggers trigger={task.trigger} />}
        </FlexColumn>
      </TaskDetailLayout.Content>

      <TaskDetailLayout.Footer>
        <Button variant='Primary' onClick={handleCtaPress}>
          {isCompleted || isExpired
            ? i18n._(globalDefinitions.close)
            : t({
                id: 'message-detail.cta.start',
                message: 'Begrepen en gelezen',
              })}
        </Button>
      </TaskDetailLayout.Footer>
    </TaskDetailLayout>
  )
}
